import { useMutation } from '@apollo/client';
import type {
  InsertDepartmentTypeWithOptionalGroupIdMutation,
  InsertDepartmentTypeWithOptionalGroupIdMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertDepartmentTypeWithOptionalGroupIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertDepartmentTypeWithOptionalGroupIdTRPC } from './useInsertDepartmentTypeWithOptionalGroupIdTRPC';

export const useInsertDepartmentTypeWithOptionalGroupId = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertDepartmentTypeWithOptionalGroupIdGraphQL, graphqlState] =
    useMutation(InsertDepartmentTypeWithOptionalGroupIdDocument, {
      update: (cache) => {
        evictField(cache, 'department_type');
        evictField(cache, 'department_type_group');
      },
    });

  // tRPC mutation
  const trpcMutation = useInsertDepartmentTypeWithOptionalGroupIdTRPC();

  const insertDepartmentTypeWithOptionalGroupId = async (
    variables: InsertDepartmentTypeWithOptionalGroupIdMutationVariables
  ): Promise<InsertDepartmentTypeWithOptionalGroupIdMutation> => {
    if (trpcEnabled) {
      if (!variables.Name) {
        throw new Error('Name is required');
      }

      return trpcMutation.insertDepartmentTypeWithOptionalGroupId({
        Name: variables.Name,
        Description: variables.Description,
        DepartmentTypeGroupId: variables.DepartmentTypeGroupId,
      });
    }

    const result = await insertDepartmentTypeWithOptionalGroupIdGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to insert department type');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertDepartmentTypeWithOptionalGroupId,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
      data: trpcMutation.data,
    };
  }

  return {
    insertDepartmentTypeWithOptionalGroupId,
    loading: graphqlState.loading,
    error: graphqlState.error,
    data: graphqlState.data,
  };
};
